import _ from 'underscore';
import { hasPlaceHolderStores } from '@bingads-webui-campaign-react/gmc-to-mmc-mapping-grid-shared';
import { INotification, TargetComponent, NotificationLevel, DismissMode, ScopeLevel, IContext } from '../../types';
import { i18nMap, shouldRender, isAvailable, isBillingLinkPermission } from '../../utils';

/**
 * ManualTest:
 * 1. Prepare fiddler mock data/real failed import
 * 2. Lauch site with fiddler opened
 * 3. Check the notificaiton in UNQ
 * 4. Go to import schedule page
 */

export interface IDraftStoreNotification{
  HasPlaceholderStores: boolean,
}

export class DraftStoreNotification {
  context: IContext;
  i18n: any;
  availableScopes: ScopeLevel[];

  constructor(context: IContext) {
    this.context = context;
    this.i18n = context.getNewI18n();
    this.availableScopes = [ScopeLevel.account, ScopeLevel.customer];
  }

  async getDraftStoreNotifications() {
    const { MockTopBanner } = this.context.getCurrentPermissions().dynamic;
    if (MockTopBanner) {
      return [];
    }

    if (!this.context.getUserContext().CurrentUser.IsInternal && isBillingLinkPermission(this.context.getSettingsContext())) {
      return [];
    }

    // Only get this notification under available scope
    if (!isAvailable(this.context.getCurrentScope(), this.availableScopes)) {
      return [];
    }

    // Only get this notification if change scope is bigger than customer level
    if (!shouldRender(ScopeLevel.customer, this.context.getChangeScope())) {
      return [];
    }

    // Get notifications
    const draftStoreNotification: IDraftStoreNotification = await this.getNotifications();
    const notifications: INotification[] = [];

    // Draft store notification
    if (this.hasAnyDraftStoreNotifications(draftStoreNotification)) {
      notifications.push({
        id: 'DraftStoreSetup',
        condition: true,
        target: TargetComponent.UnifiedQueue,
        dismissMode: DismissMode.session,
        severity: NotificationLevel.Warning,
        scopeLevel: ScopeLevel.customer,
        availableScopes: this.availableScopes,
        title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_DraftStoreSetupInfoText_Title),
        message: this.i18n.getString(i18nMap.UnifiedNotificationQueue_DraftStoreSetupInfoText_Message),
        actionList: [
          {
            text: this.i18n.getString(i18nMap.Notification_Action_View),
            action: () => {
              this.context.route.goMerchantCenterDraftStores();
            },
            callbackType: 'closeQueue',
            extraClassNames: ['draft-store-setup-redirect-button'],
          },
          {
            text: this.i18n.getString(i18nMap.LearnMore),
            extraClassNames: ['HelpTopic'],
            extraAttrs: {
              query: 'help:ext51085',
            },
          },
        ],
      });
    }

    return _.compact(notifications);
  }

  async getNotifications(): Promise<IDraftStoreNotification> {
    const { uid, aid, cid } = this.context.getCurrentScope().values;
    const smallToken = this.context.getServerContext().Token;

    return (await hasPlaceHolderStores({
      uid, cid, aid, smallToken,
    })) as IDraftStoreNotification;
  }

  hasAnyDraftStoreNotifications(draftStoreNotification: IDraftStoreNotification) {
    return draftStoreNotification.HasPlaceholderStores;
  }
}
